import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import styled from "@emotion/styled";
import { client, urlFor } from "../sanity/sanityClient";
import { toggleFollow, checkIsFollowing } from "../services/feed";
import {
  FiHeart,
  FiPlay,
  FiPause,
  FiTrash2,
  FiShoppingBag,
  FiMessageCircle,
  FiUserPlus,
  FiUserCheck,
  FiChevronLeft,
  FiShare2,
  FiDownload,
} from "react-icons/fi";
import { AiFillHeart, AiOutlineEye } from "react-icons/ai";

/**
 * ReplayFeed — a single-column, TikTok/Reels-style vertical feed of
 * ended live-stream replays (the 24-hour recordings from
 * GoLiveControlPanel.js's client-side MediaRecorder pipeline).
 *
 * RANKING (see loadReplays): followed hosts first, then hosts whose
 * OTHER replay the viewer has previously liked, then everyone else,
 * shuffled for discovery. See the file-level comment history for
 * the fuller rationale — unchanged from the prior version.
 *
 * THIS REVISION adds:
 *  - View counts, incremented once per click-to-play (see
 *    handlePlayToggle) — literally per click, not deduped per
 *    viewer/session, matching what was asked for.
 *  - The "Message" control is now hidden for the replay's owner —
 *    there's no reason to show a host a link to message themselves.
 *  - Cards with no coverImage no longer show a blank/dark
 *    placeholder. Instead, the video element itself is mounted
 *    persistently (not just when playing) and paints its own first
 *    frame as a thumbnail, using preload="metadata" — but ONLY once
 *    an IntersectionObserver confirms the card is actually visible
 *    (or about to be). Off-screen cards sit at preload="none",
 *    downloading nothing, which is what keeps this from turning into
 *    a bandwidth bill for every card in a long feed — the same
 *    lazy-load-as-you-scroll pattern TikTok/Reels-style feeds use.
 *  - A scrub progress bar + elapsed/duration readout while a replay
 *    is playing (inline and fullscreen). See handlePlayToggle,
 *    handleVideoTimeUpdate, handleVideoLoadedMetadataDuration,
 *    handleSeek and renderProgressBar below. Pausing keeps position
 *    (native <video> behavior — nothing extra needed there); the one
 *    addition is that resuming a replay that already played to the
 *    end restarts it from 0 instead of trying to "resume" past the
 *    end, which would otherwise look like nothing happened.
 */

const isMobileViewport = () => window.matchMedia("(max-width: 768px)").matches;

const shuffle = (array) => {
  const copy = [...array];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
};

// mm:ss (hh:mm:ss once an hour is crossed — replays can run long).
const formatTime = (seconds) => {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const totalSeconds = Math.floor(seconds);
  const hrs = Math.floor(totalSeconds / 3600);
  const mins = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;
  if (hrs > 0) {
    return `${hrs}:${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  }
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

export default function ReplayFeed() {
  const navigate = useNavigate();

  const [currentUser, setCurrentUser] = useState(null);
  const [replays, setReplays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [followingMap, setFollowingMap] = useState({});
  const [followBusyId, setFollowBusyId] = useState(null);
  const [likeBusyId, setLikeBusyId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [downloadProgress, setDownloadProgress] = useState(null); // 0-100, or null when not watermarking (plain fallback / not downloading)

  const [isMobile, setIsMobile] = useState(isMobileViewport());
  const [playingId, setPlayingId] = useState(null); // inline-playing card (desktop) or the one about to go fullscreen (mobile)
  const [fullscreenId, setFullscreenId] = useState(null); // mobile-only full-viewport overlay target

  // Replay ids that have started playback at least once. For cards
  // with a coverImage, the <video> element is normally swapped out
  // for the static <Thumbnail> whenever nothing is playing — but if
  // that swap happens on PAUSE too, pausing unmounts the video
  // entirely, so a second click has to mount a brand-new element and
  // loses currentTime, which looks like the video "restarting"
  // instead of resuming. Once a card is in this set, its <CardVideo>
  // stays mounted (just paused) so position survives pause/resume,
  // exactly like the no-cover-image "paint" video already does.
  const [startedIds, setStartedIds] = useState(() => new Set());

  // { [replayId]: { currentTime, duration } } — drives the scrub bar
  // and the elapsed/duration text for whichever replay is playing.
  const [playbackProgress, setPlaybackProgress] = useState({});

  const videoRefs = useRef({}); // replayId -> <video> element, shared between inline and fullscreen renders

  // ---------------------------------------------------------------
  // Visibility tracking for the no-cover-image "video paint" — one
  // shared IntersectionObserver instance rather than one per card,
  // since that's the cheaper pattern once a feed has more than a
  // handful of cards. rootMargin gives a little lead time so the
  // frame has already painted by the time a card fully scrolls into
  // view, instead of visibly popping in.
  // ---------------------------------------------------------------
  const [intersectingIds, setIntersectingIds] = useState(() => new Set());
  const observerRef = useRef(null);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        setIntersectingIds((prev) => {
          const next = new Set(prev);
          entries.forEach((entry) => {
            const id = entry.target.dataset.replayId;
            if (!id) return;
            if (entry.isIntersecting) next.add(id);
            else next.delete(id);
          });
          return next;
        });
      },
      { rootMargin: "200px 0px", threshold: 0.1 }
    );

    return () => observerRef.current?.disconnect();
  }, []);

  // Combined ref callback for the no-cover-image "paint" video: keeps
  // it registered in videoRefs (same as every other video element, so
  // play/pause control is uniform) AND registers it with the shared
  // IntersectionObserver so its preload level can react to visibility.
  const setPaintVideoRef = useCallback((el, replayId) => {
    videoRefs.current[replayId] = el;
    if (el && observerRef.current) {
      el.dataset.replayId = replayId;
      observerRef.current.observe(el);
    }
  }, []);

  // Some mobile browsers don't actually paint a visible frame purely
  // from preload="metadata" — a well-known workaround is nudging
  // currentTime forward by a hair once metadata is available, which
  // forces a frame decode/paint without meaningfully affecting
  // playback position or triggering a real data download beyond what
  // metadata-level preload already fetches.
  const handlePaintLoadedMetadata = (e) => {
    try {
      if (e.target.currentTime === 0) {
        e.target.currentTime = 0.1;
      }
    } catch (err) {
      // Some browsers throw if the video isn't seekable yet at this
      // point — harmless to ignore, worst case the frame just doesn't
      // paint early and shows once actual playback starts instead.
    }
  };

  // ---------------------------------------------------------------
  // Playback progress — shared by every <video> in the feed. Fed by
  // each video's own onTimeUpdate/onLoadedMetadata, keyed by replay
  // id so switching which card is playing doesn't mix up state.
  // ---------------------------------------------------------------
  const handleVideoTimeUpdate = (replayId) => (e) => {
    const { currentTime, duration } = e.target;
    setPlaybackProgress((prev) => {
      // Once we have a real duration (see resolveRealDuration below),
      // keep it — MediaRecorder-produced webm blobs often don't embed
      // a real Duration in the container, so the browser's own
      // `duration` reading can drift/shrink mid-playback as more of
      // the file streams in. Re-adopting that moving number here is
      // what made the bar appear to "finish" before the video did.
      const existingDuration = prev[replayId]?.duration;
      if (existingDuration) {
        return { ...prev, [replayId]: { currentTime, duration: existingDuration } };
      }
      return {
        ...prev,
        [replayId]: {
          currentTime,
          duration: Number.isFinite(duration) ? duration : 0,
        },
      };
    });
  };

  // MediaRecorder-produced webm blobs commonly omit the container's
  // Duration element entirely, so `videoEl.duration` reads as
  // Infinity (or another unreliable value) right after metadata
  // loads. The standard workaround is to seek to a timestamp far
  // beyond any real length — that forces the browser to scan for the
  // actual end and fire 'durationchange' with the correct value —
  // then reset playback position back to where it should be.
  const resolveRealDuration = (videoEl, replayId, resumeAt) => {
    const handleDurationChange = () => {
      if (!Number.isFinite(videoEl.duration)) return;
      videoEl.removeEventListener("durationchange", handleDurationChange);
      videoEl.currentTime = resumeAt;
      setPlaybackProgress((prev) => ({
        ...prev,
        [replayId]: { currentTime: resumeAt, duration: videoEl.duration },
      }));
    };

    videoEl.addEventListener("durationchange", handleDurationChange);
    videoEl.currentTime = 1e101;
  };

  const handleVideoLoadedMetadataDuration = (replayId) => (e) => {
    const videoEl = e.target;
    if (!Number.isFinite(videoEl.duration)) {
      resolveRealDuration(videoEl, replayId, videoEl.currentTime || 0);
      return;
    }
    setPlaybackProgress((prev) => ({
      ...prev,
      [replayId]: {
        currentTime: prev[replayId]?.currentTime || 0,
        duration: videoEl.duration,
      },
    }));
  };

  // Scrub bar drag/click — reads straight off the <input type="range">
  // so browser-native dragging, keyboard arrows, and click-to-seek all
  // just work without any custom hit-testing.
  const handleSeek = (e, replay) => {
    const videoEl = videoRefs.current[replay._id];
    const value = parseFloat(e.target.value);
    if (!videoEl || Number.isNaN(value)) return;
    videoEl.currentTime = value;
    setPlaybackProgress((prev) => ({
      ...prev,
      [replay._id]: { ...prev[replay._id], currentTime: value },
    }));
  };

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const handleChange = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", handleChange);
    return () => mq.removeEventListener("change", handleChange);
  }, []);

  useEffect(() => {
    const userData = localStorage.getItem(process.env.REACT_APP_LOCALHOST_KEY);
    if (userData) setCurrentUser(JSON.parse(userData));
  }, []);

  // ---------------------------------------------------------------
  // Fetch + rank
  // ---------------------------------------------------------------
  const loadReplays = useCallback(async () => {
    setLoading(true);
    try {
      const docs = await client.fetch(
        `*[_type == "liveSession" && isLive == false && defined(recordingAsset) && recordingExpiresAt > now()] | order(startTime desc){
          ...,
          "recordingAssetUrl": recordingAsset.asset->url,
          "featuredProduct": *[_id == ^.featuredProductId][0]{ _id, name, price, currency, image, slug }
        }`
      );

      if (!currentUser?._id) {
        setReplays(docs);
        return;
      }

      const uniqueHostIds = [...new Set(docs.map((d) => d.hostUserId))];
      const followPairs = await Promise.all(
        uniqueHostIds.map((hostId) =>
          checkIsFollowing(currentUser._id, hostId)
            .then((isFollowing) => [hostId, isFollowing])
            .catch(() => [hostId, false])
        )
      );
      const followMap = Object.fromEntries(followPairs);
      setFollowingMap(followMap);

      const likedHostIds = new Set(
        docs
          .filter((d) => d.replayLikedBy?.includes(currentUser._id))
          .map((d) => d.hostUserId)
      );

      const followedTier = [];
      const engagedTier = [];
      const restTier = [];

      docs.forEach((doc) => {
        if (followMap[doc.hostUserId]) {
          followedTier.push(doc);
        } else if (likedHostIds.has(doc.hostUserId)) {
          engagedTier.push(doc);
        } else {
          restTier.push(doc);
        }
      });

      setReplays([...followedTier, ...engagedTier, ...shuffle(restTier)]);
    } catch (err) {
      console.error("Failed to load replays:", err);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    loadReplays();
  }, [loadReplays]);

  // ---------------------------------------------------------------
  // Play / pause — also where the view count increments, exactly
  // once per click that STARTS playback (the pause branch below
  // returns early and never touches the count).
  //
  // Resuming: native <video> pause/play already preserves currentTime
  // on its own, so a plain pause-then-play "just resumes" with no
  // extra code. The one edge case handled explicitly here is a video
  // that already played to the end — el.ended is true and currentTime
  // sits at duration, so calling play() again would appear to do
  // nothing. Detect that and rewind to 0 first so replaying it after
  // it finishes behaves like starting over, not like a stuck resume.
  // ---------------------------------------------------------------
  const handlePlayToggle = (replay) => {
    const videoEl = videoRefs.current[replay._id];

    if (playingId === replay._id) {
      videoEl?.pause();
      setPlayingId(null);
      if (isMobile) setFullscreenId(null);
      return;
    }

    if (playingId && videoRefs.current[playingId]) {
      videoRefs.current[playingId].pause();
    }

    setPlayingId(replay._id);
    if (isMobile) setFullscreenId(replay._id);
    setStartedIds((prev) => (prev.has(replay._id) ? prev : new Set(prev).add(replay._id)));

    requestAnimationFrame(() => {
      const el = videoRefs.current[replay._id];
      if (!el) return;
      if (el.ended) {
        el.currentTime = 0;
        setPlaybackProgress((prev) => ({
          ...prev,
          [replay._id]: { ...prev[replay._id], currentTime: 0 },
        }));
      }
      el.play().catch((err) => {
        console.error("Playback failed:", err);
      });
    });

    // View count — optimistic local bump immediately, then a durable
    // atomic increment on the document. setIfMissing first because
    // sessions created before this field existed (or any created via
    // GoLiveControlPanel.js's client.create, which doesn't set
    // viewCount explicitly) don't have a numeric value yet — Sanity's
    // inc() requires the field to already exist as a number, it
    // doesn't upsert from undefined on its own.
    setReplays((prev) =>
      prev.map((r) => (r._id === replay._id ? { ...r, viewCount: (r.viewCount || 0) + 1 } : r))
    );
    client
      .patch(replay._id)
      .setIfMissing({ viewCount: 0 })
      .inc({ viewCount: 1 })
      .commit()
      .catch((err) => console.error("Failed to record view:", err));
  };

  const closeFullscreen = () => {
    if (fullscreenId && videoRefs.current[fullscreenId]) {
      videoRefs.current[fullscreenId].pause();
    }
    setFullscreenId(null);
    setPlayingId(null);
  };

  // ---------------------------------------------------------------
  // Follow
  // ---------------------------------------------------------------
  const handleFollowToggle = async (e, hostUserId) => {
    e.stopPropagation();
    if (!currentUser) {
      navigate("/login");
      return;
    }
    if (followBusyId) return;

    setFollowBusyId(hostUserId);
    const optimistic = !followingMap[hostUserId];
    setFollowingMap((prev) => ({ ...prev, [hostUserId]: optimistic }));

    try {
      const result = await toggleFollow(currentUser._id, hostUserId);
      setFollowingMap((prev) => ({ ...prev, [hostUserId]: result.following }));
    } catch (err) {
      console.error("Follow toggle failed:", err);
      setFollowingMap((prev) => ({ ...prev, [hostUserId]: !optimistic }));
    } finally {
      setFollowBusyId(null);
    }
  };

  // ---------------------------------------------------------------
  // Like — persisted on the doc itself (replayLikedBy).
  // ---------------------------------------------------------------
  const handleLikeToggle = async (e, replay) => {
    e.stopPropagation();
    if (!currentUser) {
      navigate("/login");
      return;
    }
    if (likeBusyId) return;

    const alreadyLiked = replay.replayLikedBy?.includes(currentUser._id);
    setLikeBusyId(replay._id);

    setReplays((prev) =>
      prev.map((r) =>
        r._id === replay._id
          ? {
              ...r,
              replayLikedBy: alreadyLiked
                ? (r.replayLikedBy || []).filter((id) => id !== currentUser._id)
                : [...(r.replayLikedBy || []), currentUser._id],
            }
          : r
      )
    );

    try {
      if (alreadyLiked) {
        await client
          .patch(replay._id)
          .unset([`replayLikedBy[@ == "${currentUser._id}"]`])
          .commit();
      } else {
        await client
          .patch(replay._id)
          .setIfMissing({ replayLikedBy: [] })
          .append("replayLikedBy", [currentUser._id])
          .commit();
      }
    } catch (err) {
      console.error("Like toggle failed:", err);
      setReplays((prev) =>
        prev.map((r) =>
          r._id === replay._id
            ? {
                ...r,
                replayLikedBy: alreadyLiked
                  ? [...(r.replayLikedBy || []), currentUser._id]
                  : (r.replayLikedBy || []).filter((id) => id !== currentUser._id),
              }
            : r
        )
      );
    } finally {
      setLikeBusyId(null);
    }
  };

  // ---------------------------------------------------------------
  // Delete (owner only)
  // ---------------------------------------------------------------
  const handleDelete = async (e, replay) => {
    e.stopPropagation();
    if (currentUser?._id !== replay.hostUserId) return;

    const confirmed = window.confirm("Delete this replay? This can't be undone.");
    if (!confirmed) return;

    setDeletingId(replay._id);
    try {
      await client
        .patch(replay._id)
        .unset(["recordingAsset", "recordingExpiresAt"])
        .commit();

      const assetId = replay.recordingAsset?.asset?._ref;
      if (assetId) {
        await client.delete(assetId).catch((err) => {
          console.error("Failed to delete underlying asset (doc fields already cleared):", err);
        });
      }

      setReplays((prev) => prev.filter((r) => r._id !== replay._id));
      if (fullscreenId === replay._id) setFullscreenId(null);
      if (playingId === replay._id) setPlayingId(null);
      setPlaybackProgress((prev) => {
        const next = { ...prev };
        delete next[replay._id];
        return next;
      });
      setStartedIds((prev) => {
        if (!prev.has(replay._id)) return prev;
        const next = new Set(prev);
        next.delete(replay._id);
        return next;
      });
    } catch (err) {
      console.error("Failed to delete replay:", err);
      window.alert("Failed to delete this replay. Please try again.");
    } finally {
      setDeletingId(null);
    }
  };

  // ---------------------------------------------------------------
  // Share — links back into THIS app's own replay viewer (Live.js
  // already renders a replay screen at /live/:sessionId for ended
  // sessions with a still-valid recording — see its hasValidReplay
  // branch), not a bare video file. That matters: a recipient who
  // opens the shared link gets the full commerce context — the
  // featured product, the Follow button, the host's name — not just
  // a video with none of that. Uses the native share sheet where
  // available (near-universal on mobile, including installed PWAs),
  // falling back to clipboard, then a manual prompt as a last resort
  // for browsers with neither.
  // ---------------------------------------------------------------
  const handleShare = async (e, replay) => {
    e.stopPropagation();
    const shareUrl = `${window.location.origin}/#/live/${replay._id}`;
    const shareData = {
      title: replay.title || "Live stream replay",
      text: `${replay.hostName} on BridgenApp: ${replay.title}`,
      url: shareUrl,
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        // AbortError just means the user closed the native share
        // sheet without picking anything — not a real failure.
        if (err.name !== "AbortError") {
          console.error("Share failed:", err);
        }
      }
      return;
    }

    if (navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(shareUrl);
        window.alert("Link copied to clipboard!");
      } catch (err) {
        console.error("Clipboard copy failed:", err);
      }
      return;
    }

    window.prompt("Copy this link to share:", shareUrl);
  };

  // ---------------------------------------------------------------
  // Watermark — "BridgenApp Sell&Shop" burned into the bottom-right
  // corner of every downloaded replay. "BridgenApp" uses a
  // navy-to-orange gradient matching the brand pairing already used
  // everywhere else in the app (the Live logo, the bottom nav's
  // active-orange-on-navy scheme); "Sell&Shop" sits beside it in
  // white. Drawn on a semi-transparent rounded pill so it stays
  // legible regardless of what's happening in the underlying frame.
  // ---------------------------------------------------------------
  const WATERMARK_PRIMARY = "BridgenApp";
  const WATERMARK_SECONDARY = " Sell&Shop";
  const BRAND_NAVY = "#0f1f3d";
  const BRAND_ORANGE = "#f97316";

  const drawWatermarkedFrame = (ctx, sourceVideo, canvas, gradientCacheRef) => {
    ctx.drawImage(sourceVideo, 0, 0, canvas.width, canvas.height);

    const fontSize = Math.max(14, Math.round(canvas.width * 0.028));
    ctx.font = `700 ${fontSize}px sans-serif`;
    ctx.textBaseline = "middle";

    const primaryWidth = ctx.measureText(WATERMARK_PRIMARY).width;
    const secondaryWidth = ctx.measureText(WATERMARK_SECONDARY).width;

    const paddingX = fontSize * 0.7;
    const paddingY = fontSize * 0.5;
    const boxWidth = primaryWidth + secondaryWidth + paddingX * 2;
    const boxHeight = fontSize + paddingY * 2;
    const boxX = canvas.width - boxWidth - fontSize * 0.6;
    const boxY = canvas.height - boxHeight - fontSize * 0.6;
    const radius = boxHeight / 2;

    ctx.fillStyle = "rgba(0, 0, 0, 0.45)";
    ctx.beginPath();
    ctx.moveTo(boxX + radius, boxY);
    ctx.arcTo(boxX + boxWidth, boxY, boxX + boxWidth, boxY + boxHeight, radius);
    ctx.arcTo(boxX + boxWidth, boxY + boxHeight, boxX, boxY + boxHeight, radius);
    ctx.arcTo(boxX, boxY + boxHeight, boxX, boxY, radius);
    ctx.arcTo(boxX, boxY, boxX + boxWidth, boxY, radius);
    ctx.closePath();
    ctx.fill();

    const textY = boxY + boxHeight / 2;
    const textStartX = boxX + paddingX;

    // The gradient's coordinates are fixed for the lifetime of this
    // canvas (position never changes frame-to-frame), so it's built
    // once and reused — recreating a CanvasGradient object 30 times a
    // second for the length of a whole stream would be wasteful.
    if (!gradientCacheRef.current) {
      const gradient = ctx.createLinearGradient(textStartX, 0, textStartX + primaryWidth, 0);
      gradient.addColorStop(0, BRAND_NAVY);
      gradient.addColorStop(1, BRAND_ORANGE);
      gradientCacheRef.current = gradient;
    }

    ctx.fillStyle = gradientCacheRef.current;
    ctx.fillText(WATERMARK_PRIMARY, textStartX, textY);

    ctx.fillStyle = "#ffffff";
    ctx.fillText(WATERMARK_SECONDARY, textStartX + primaryWidth, textY);
  };

  const canWatermarkDownloads = () =>
    typeof window !== "undefined" &&
    !!window.MediaRecorder &&
    typeof HTMLVideoElement !== "undefined" &&
    !!HTMLVideoElement.prototype.captureStream;

  const getSupportedRecorderMimeType = () => {
    const candidates = [
      "video/webm;codecs=vp9,opus",
      "video/webm;codecs=vp8,opus",
      "video/webm",
    ];
    return candidates.find((type) => window.MediaRecorder?.isTypeSupported?.(type)) || null;
  };

  const triggerBlobDownload = (blob, replay, extension) => {
    const blobUrl = URL.createObjectURL(blob);
    const safeHostName = (replay.hostName || "bridgenapp").replace(/[^a-z0-9]+/gi, "-");

    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = `${safeHostName}-replay-${replay._id}.${extension}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
  };

  // Fallback for browsers that can't do canvas+MediaRecorder capture
  // (canWatermarkDownloads() returned false) — same plain fetch-to-Blob
  // approach as before, just without the sticker. See the earlier
  // comment history for why a plain <a download> isn't reliable here.
  const downloadPlain = async (replay) => {
    const response = await fetch(replay.recordingAssetUrl);
    if (!response.ok) throw new Error(`Fetch failed with status ${response.status}`);
    const blob = await response.blob();
    const extension = replay.recordingAssetUrl.split(".").pop()?.split("?")[0] || "mp4";
    triggerBlobDownload(blob, replay, extension);
  };

  const handleDownload = async (e, replay) => {
    e.stopPropagation();
    if (downloadingId) return;

    setDownloadingId(replay._id);
    setDownloadProgress(0);

    if (!canWatermarkDownloads()) {
      try {
        await downloadPlain(replay);
      } catch (err) {
        console.error("Download failed:", err);
        window.alert("Failed to download this video. Please try again.");
      } finally {
        setDownloadingId(null);
        setDownloadProgress(null);
      }
      return;
    }

    const mimeType = getSupportedRecorderMimeType();
    if (!mimeType) {
      // Capture APIs exist but no usable output format does — fall
      // back rather than fail outright.
      try {
        await downloadPlain(replay);
      } catch (err) {
        console.error("Download failed:", err);
        window.alert("Failed to download this video. Please try again.");
      } finally {
        setDownloadingId(null);
        setDownloadProgress(null);
      }
      return;
    }

    // Cleanup handles are collected as setup proceeds, so a failure
    // partway through can still tear down everything already created
    // instead of leaking audio contexts / hidden video elements.
    let sourceVideo, audioContext, canvas, mediaRecorder, rafId;
    const cleanup = () => {
      cancelAnimationFrame(rafId);
      sourceVideo?.pause();
      sourceVideo?.remove();
      audioContext?.close().catch(() => {});
    };

    try {
      sourceVideo = document.createElement("video");
      sourceVideo.crossOrigin = "anonymous"; // required to draw a cross-origin frame to canvas without tainting it — depends on Sanity's CDN sending permissive CORS headers on the asset, which it does for file assets by default
      sourceVideo.src = replay.recordingAssetUrl;
      sourceVideo.style.display = "none";
      document.body.appendChild(sourceVideo);

      await new Promise((resolve, reject) => {
        sourceVideo.onloadedmetadata = resolve;
        sourceVideo.onerror = () => reject(new Error("Failed to load source video"));
      });

      canvas = document.createElement("canvas");
      canvas.width = sourceVideo.videoWidth;
      canvas.height = sourceVideo.videoHeight;
      const ctx = canvas.getContext("2d");

      // Audio routing: play the source video (required for
      // captureStream to have real-time frames/audio to capture) but
      // silence what actually reaches the user's speakers, while a
      // SEPARATE tap on the same audio source still carries full
      // volume into the recorded stream. Without this, the replay's
      // audio would audibly play through the device speakers for the
      // entire length of the download.
      audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const sourceNode = audioContext.createMediaElementSource(sourceVideo);

      const silentGain = audioContext.createGain();
      silentGain.gain.value = 0;
      sourceNode.connect(silentGain);
      silentGain.connect(audioContext.destination);

      const audioDestination = audioContext.createMediaStreamDestination();
      sourceNode.connect(audioDestination);

      const canvasStream = canvas.captureStream(30);
      const combinedStream = new MediaStream([
        ...canvasStream.getVideoTracks(),
        ...audioDestination.stream.getAudioTracks(),
      ]);

      const chunks = [];
      mediaRecorder = new MediaRecorder(combinedStream, { mimeType });
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunks.push(event.data);
      };

      const recordingDone = new Promise((resolve) => {
        mediaRecorder.onstop = resolve;
      });

      const gradientCacheRef = { current: null };

      const drawLoop = () => {
        if (sourceVideo.ended || sourceVideo.paused) return;
        drawWatermarkedFrame(ctx, sourceVideo, canvas, gradientCacheRef);
        if (sourceVideo.duration) {
          setDownloadProgress(Math.min(99, Math.round((sourceVideo.currentTime / sourceVideo.duration) * 100)));
        }
        rafId = requestAnimationFrame(drawLoop);
      };

      sourceVideo.onended = () => {
        mediaRecorder.stop();
      };

      mediaRecorder.start();
      await sourceVideo.play();
      drawLoop();

      await recordingDone;

      const blob = new Blob(chunks, { type: mimeType });
      const extension = mimeType.includes("mp4") ? "mp4" : "webm";
      setDownloadProgress(100);
      triggerBlobDownload(blob, replay, extension);
    } catch (err) {
      console.error("Watermarked download failed, falling back to plain download:", err);
      try {
        await downloadPlain(replay);
      } catch (fallbackErr) {
        console.error("Fallback download also failed:", fallbackErr);
        window.alert("Failed to download this video. Please try again.");
      }
    } finally {
      cleanup();
      setDownloadingId(null);
      setDownloadProgress(null);
    }
  };

  // ---------------------------------------------------------------
  // Progress bar — scrub slider + elapsed/duration readout. Only
  // rendered for the card that's currently playing. Uses a native
  // <input type="range"> so drag, click, keyboard, and touch seeking
  // all come for free instead of needing custom hit-testing.
  // ---------------------------------------------------------------
  const renderProgressBar = (replay, { light } = {}) => {
    const progress = playbackProgress[replay._id];
    const duration = progress?.duration || 0;
    const currentTime = Math.min(progress?.currentTime || 0, duration || progress?.currentTime || 0);
    const pct = duration ? Math.min(100, (currentTime / duration) * 100) : 0;

    return (
      <ProgressBarRow onClick={(e) => e.stopPropagation()} onTouchStart={(e) => e.stopPropagation()}>
        <ProgressTime light={light}>{formatTime(currentTime)}</ProgressTime>
        <ProgressSlider
          type="range"
          min={0}
          max={duration || 0}
          step={0.1}
          value={currentTime}
          disabled={!duration}
          onChange={(e) => handleSeek(e, replay)}
          onClick={(e) => e.stopPropagation()}
          style={{
            background: `linear-gradient(to right, #f97316 0%, #f97316 ${pct}%, rgba(255,255,255,0.35) ${pct}%, rgba(255,255,255,0.35) 100%)`,
          }}
        />
        <ProgressTime light={light}>{formatTime(duration)}</ProgressTime>
      </ProgressBarRow>
    );
  };

  // ---------------------------------------------------------------
  // Card content — shared between inline and fullscreen rendering.
  // ---------------------------------------------------------------
  const renderControls = (replay, { compact }) => {
    const isOwner = currentUser?._id === replay.hostUserId;
    const isFollowing = !!followingMap[replay.hostUserId];
    const isLiked = replay.replayLikedBy?.includes(currentUser?._id);
    const likeCount = replay.replayLikedBy?.length || 0;

    return (
      <ControlsRow compact={compact}>
        <ControlButton onClick={(e) => handleLikeToggle(e, replay)} disabled={likeBusyId === replay._id}>
          {isLiked ? <AiFillHeart color="#f97316" /> : <FiHeart />}
          <span>{likeCount}</span>
        </ControlButton>

        {!isOwner && currentUser && (
          <ControlButton
            onClick={(e) => handleFollowToggle(e, replay.hostUserId)}
            disabled={followBusyId === replay.hostUserId}
            active={isFollowing}
          >
            {isFollowing ? <FiUserCheck /> : <FiUserPlus />}
            <span>{isFollowing ? "Following" : "Follow"}</span>
          </ControlButton>
        )}

        <ControlLink to={`/shop/${replay.hostUserId}`} onClick={(e) => e.stopPropagation()}>
          <FiShoppingBag />
          <span>Store</span>
        </ControlLink>

        {/* Message — hidden for the owner. There's no reason to show
            a host a link to message themselves about their own replay. */}
        {!isOwner && (
          <ControlLink to={`/chat/${replay.hostUserId}`} onClick={(e) => e.stopPropagation()}>
            <FiMessageCircle />
            <span>Message</span>
          </ControlLink>
        )}

        <ControlButton onClick={(e) => handleShare(e, replay)}>
          <FiShare2 />
          <span>Share</span>
        </ControlButton>

        <ControlButton onClick={(e) => handleDownload(e, replay)} disabled={downloadingId === replay._id}>
          <FiDownload />
          <span>
            {downloadingId === replay._id
              ? downloadProgress !== null
                ? `${downloadProgress}%`
                : "Downloading…"
              : "Download"}
          </span>
        </ControlButton>

        {isOwner && (
          <ControlButton
            onClick={(e) => handleDelete(e, replay)}
            disabled={deletingId === replay._id}
            danger
          >
            <FiTrash2 />
            <span>{deletingId === replay._id ? "Deleting…" : "Delete"}</span>
          </ControlButton>
        )}
      </ControlsRow>
    );
  };

  const renderHostRow = (replay, { compact = false } = {}) => (
    <HostRow>
      {replay.hostAvatarUrl ? (
        <HostAvatar
          src={
            replay.hostAvatarUrl.startsWith("http")
              ? replay.hostAvatarUrl
              : `data:image/svg+xml;base64,${replay.hostAvatarUrl}`
          }
          alt={replay.hostName}
        />
      ) : (
        <HostAvatarPlaceholder>
          {replay.hostName?.charAt(0)?.toUpperCase()}
        </HostAvatarPlaceholder>
      )}

      <HostName light={compact}>
        {replay.hostName}
      </HostName>

      {followingMap[replay.hostUserId] && (
        <FollowingTag>Following</FollowingTag>
      )}
    </HostRow>
  );

  // ---------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------
  return (
    <PageContainer>
      <TopBar>
        <BackLink onClick={() => navigate(-1)}>
          <FiChevronLeft size={20} />
        </BackLink>
        <Title>Replays</Title>
        <div style={{ width: 20 }} />
      </TopBar>

      {loading ? (
        <CenterMessage>Loading replays…</CenterMessage>
      ) : replays.length === 0 ? (
        <CenterMessage>
          No replays available right now.
          <br />
          <Link to="/live-discovery" style={{ color: "#f97316", fontWeight: 600 }}>
            See who's live now
          </Link>
        </CenterMessage>
      ) : (
        <Feed>
          {replays.map((replay) => {
            const isPlayingInline = playingId === replay._id && fullscreenId !== replay._id;
            const hasCoverImage = !!replay.coverImage;

            return (
              <Card key={replay._id}>
                <ThumbnailWrapper onClick={() => handlePlayToggle(replay)}>
                  {hasCoverImage ? (
                    startedIds.has(replay._id) ? (
                      // Stays mounted for the rest of this card's life
                      // once playback has started at all (playing OR
                      // paused) — only the very first click, before
                      // there's anything to resume, shows the static
                      // Thumbnail instead.
                      <CardVideo
                        ref={(el) => {
                          videoRefs.current[replay._id] = el;
                        }}
                        src={replay.recordingAssetUrl}
                        playsInline
                        muted={!isPlayingInline}
                        onEnded={() => setPlayingId(null)}
                        onTimeUpdate={handleVideoTimeUpdate(replay._id)}
                        onLoadedMetadata={handleVideoLoadedMetadataDuration(replay._id)}
                      />
                    ) : (
                      <Thumbnail src={urlFor(replay.coverImage)} alt={replay.title} />
                    )
                  ) : (
                    // No cover image — the video itself paints the
                    // thumbnail instead of a blank/dark box. Always
                    // mounted (unlike the coverImage branch, where the
                    // video only mounts once playing), so its preload
                    // level can be driven by visibility below.
                    <VideoPaint
                      ref={(el) => setPaintVideoRef(el, replay._id)}
                      src={replay.recordingAssetUrl}
                      playsInline
                      muted={!isPlayingInline}
                      preload={intersectingIds.has(replay._id) ? "metadata" : "none"}
                      onLoadedMetadata={(e) => {
                        handlePaintLoadedMetadata(e);
                        handleVideoLoadedMetadataDuration(replay._id)(e);
                      }}
                      onTimeUpdate={handleVideoTimeUpdate(replay._id)}
                      onEnded={() => setPlayingId(null)}
                    />
                  )}

                  <PlayOverlay>{playingId === replay._id ? <FiPause size={28} /> : <FiPlay size={28} />}</PlayOverlay>

                  <ReplayBadge>REPLAY</ReplayBadge>

                  <ViewCountBadge>
                    <AiOutlineEye size={12} />
                    {replay.viewCount || 0}
                  </ViewCountBadge>

                  {isPlayingInline && (
                    <ThumbnailProgressOverlay>
                      {renderProgressBar(replay, { light: true })}
                    </ThumbnailProgressOverlay>
                  )}
                </ThumbnailWrapper>

                <CardBody>
                  {renderHostRow(replay)}
                  <StreamTitle>{replay.title}</StreamTitle>

                  {replay.featuredProduct && (
                    <FeaturedProductRow onClick={(e) => e.stopPropagation()}>
                      <Link to={`/category/${replay.featuredProduct.slug?.current}`} style={{ display: "flex", alignItems: "center", gap: "8px", textDecoration: "none" }}>
                        {replay.featuredProduct.image?.[0] && (
                          <FeaturedProductImage src={urlFor(replay.featuredProduct.image[0])} alt="" />
                        )}
                        <div>
                          <FeaturedProductName>{replay.featuredProduct.name}</FeaturedProductName>
                          <FeaturedProductPrice>
                            {replay.featuredProduct.currency || replay.currency || "TZS"} {replay.featuredProduct.price?.toLocaleString()}
                          </FeaturedProductPrice>
                        </div>
                      </Link>
                    </FeaturedProductRow>
                  )}

                  {renderControls(replay, { compact: false })}
                </CardBody>
              </Card>
            );
          })}
        </Feed>
      )}

      {/* ---------- MOBILE FULLSCREEN OVERLAY ---------- */}
      {isMobile && fullscreenId && (
        <FullscreenOverlay>
          {(() => {
            const replay = replays.find((r) => r._id === fullscreenId);
            if (!replay) return null;

            return (
              <>
                <FullscreenVideo
                  ref={(el) => {
                    videoRefs.current[replay._id] = el;
                  }}
                  src={replay.recordingAssetUrl}
                  playsInline
                  autoPlay
                  onEnded={() => setPlayingId(null)}
                  onClick={() => handlePlayToggle(replay)}
                  onTimeUpdate={handleVideoTimeUpdate(replay._id)}
                  onLoadedMetadata={handleVideoLoadedMetadataDuration(replay._id)}
                />

                <FullscreenTopBar>
                  <CloseButton onClick={closeFullscreen}>
                    <FiChevronLeft size={22} color="#fff" />
                  </CloseButton>
                  <ReplayBadge style={{ position: "static" }}>REPLAY</ReplayBadge>
                  <div style={{ width: 22 }} />
                </FullscreenTopBar>

                <FullscreenBottomPanel>
                  {renderProgressBar(replay, { light: true })}

                  {renderHostRow(replay, { compact: true })}

                  <StreamTitle style={{ color: "#fff" }}>
                    {replay.title}
                  </StreamTitle>

                  {renderControls(replay, { compact: true })}
                </FullscreenBottomPanel>
              </>
            );
          })()}
        </FullscreenOverlay>
      )}
    </PageContainer>
  );
}

/* ================= STYLES ================= */

const PageContainer = styled.div`
  min-height: 100vh;
  background: #f8f9fb;
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  background: white;
  border-bottom: 1px solid #eee;
  position: sticky;
  top: 0;
  z-index: 5;
`;

const BackLink = styled.div`
  cursor: pointer;
  color: #111;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 16px;
`;

const CenterMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 60vh;
  text-align: center;
  color: #666;
  padding: 20px;
`;

const Feed = styled.div`
  max-width: 600px;
  margin: 0 auto;
  padding: 16px 0 40px;
  display: flex;
  flex-direction: column;
  gap: 20px;

  @media (min-width: 769px) {
    padding: 16px 12px 40px;
  }
`;

const Card = styled.div`
  background: white;
  overflow: hidden;
  border-bottom: 1px solid #eee;

  @media (min-width: 769px) {
    border-radius: 14px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);
    border-bottom: none;
  }
`;

const ThumbnailWrapper = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 9 / 12;
  background: #000;
  cursor: pointer;
`;

const Thumbnail = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const CardVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  background: #000;
`;

// Same visual treatment as CardVideo — it's the same kind of element,
// just persistently mounted so it can paint a first frame even while
// paused, instead of only appearing once tapped.
const VideoPaint = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  background: #000;
`;

const PlayOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  background: rgba(0, 0, 0, 0.15);
  pointer-events: none;
`;

const ReplayBadge = styled.div`
  position: absolute;
  top: 10px;
  left: 10px;
  background: #2563eb;
  color: white;
  font-size: 10px;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 20px;
`;

const ViewCountBadge = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  display: flex;
  align-items: center;
  gap: 3px;
  background: rgba(0, 0, 0, 0.5);
  color: white;
  font-size: 10px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 20px;
`;

// Bottom-pinned strip over the inline thumbnail/video, only rendered
// while that card is actively playing. Gradient keeps the scrub bar
// and time readout legible over any frame content underneath.
const ThumbnailProgressOverlay = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2;
  padding: 8px 10px 10px;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.6), transparent);
`;

const ProgressBarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ProgressTime = styled.span`
  font-size: 10px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: ${(props) => (props.light ? "rgba(255, 255, 255, 0.9)" : "#333")};
  min-width: 32px;
  text-align: center;
  flex-shrink: 0;
`;

const ProgressSlider = styled.input`
  flex: 1;
  -webkit-appearance: none;
  appearance: none;
  height: 4px;
  border-radius: 2px;
  cursor: pointer;
  outline: none;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: #f97316;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.9);
    cursor: pointer;
    margin-top: -4px;
  }

  &::-moz-range-thumb {
    width: 12px;
    height: 12px;
    border: none;
    border-radius: 50%;
    background: #f97316;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.9);
    cursor: pointer;
  }

  &::-moz-range-track {
    height: 4px;
    border-radius: 2px;
    background: transparent;
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const CardBody = styled.div`
  padding: 12px;
`;

const HostRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
`;

const HostAvatar = styled.img`
  width: 26px;
  height: 26px;
  border-radius: 50%;
  object-fit: cover;
`;

const HostAvatarPlaceholder = styled.div`
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #f97316;
  color: white;
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const HostName = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${(props) => (props.light ? "#fff" : "#111")};
`;

const FollowingTag = styled.span`
  font-size: 10px;
  color: #2563eb;
  background: #eff6ff;
  padding: 2px 8px;
  border-radius: 10px;
`;

const StreamTitle = styled.p`
  font-size: 13px;
  color: #333;
  margin: 0 0 10px;
`;

const FeaturedProductRow = styled.div`
  background: #f8f9fb;
  border-radius: 10px;
  padding: 8px;
  margin-bottom: 10px;
`;

const FeaturedProductImage = styled.img`
  width: 40px;
  height: 40px;
  object-fit: cover;
  border-radius: 6px;
`;

const FeaturedProductName = styled.p`
  font-size: 12px;
  font-weight: 600;
  margin: 0;
  color: #111;
`;

const FeaturedProductPrice = styled.p`
  font-size: 11px;
  color: #f97316;
  margin: 2px 0 0;
`;

const ControlsRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${(props) => (props.compact ? "14px" : "10px")};
  flex-wrap: wrap;
`;

const ControlButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  background: ${(props) => (props.danger ? "#fef2f2" : props.active ? "#fff7ed" : "#f1f1f1")};
  color: ${(props) => (props.danger ? "#dc2626" : props.active ? "#f97316" : "#333")};
  border: none;
  border-radius: 16px;
  padding: 6px 10px;
  font-size: 11px;
  font-weight: 600;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  opacity: ${(props) => (props.disabled ? 0.6 : 1)};
`;

const ControlLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 4px;
  background: #f1f1f1;
  color: #333;
  border-radius: 16px;
  padding: 6px 10px;
  font-size: 11px;
  font-weight: 600;
  text-decoration: none;
`;

/* ---- Mobile fullscreen overlay ---- */

const FullscreenOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 100;
  background: #000;
  display: flex;
  flex-direction: column;
`;

const FullscreenVideo = styled.video`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: #000;
`;

const FullscreenTopBar = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px;
`;

const CloseButton = styled.div`
  cursor: pointer;
`;

const FullscreenBottomPanel = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2;
  padding: 14px;
  background: linear-gradient(
    to top,
    rgba(0, 0, 0, 0.85),
    transparent
  );
`;
